import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import useTranslation from '../../hooks/useTranslation';
import { notificationsAPI } from '../../services/api';
import { NOTIFICATIONS_CHANGED_EVENT } from '../../utils/notificationLinks';
import styles from './NotificationBell.module.css';

// One check a minute while the page is open, plus one on every page change. Modest on
// purpose: a badge does not need to be instant, and every open tab runs this.
export const UNREAD_POLL_MS = 60000;

/**
 * Header bell with the unread count (KAN-533). Signed-in users only. The number is the
 * server's unread_count, shown as given (capped at 99+ for space). If the count cannot be
 * fetched, the bell still shows and links to the inbox, with no number.
 */
const NotificationBell = () => {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [unread, setUnread] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const res = await notificationsAPI.getUnreadCount();
      const n = Number(res?.data?.unread_count);
      setUnread(Number.isFinite(n) && n > 0 ? n : 0);
    } catch {
      // Keep the last known count. The badge is a hint; the inbox page shows errors.
    }
  }, []);

  // On sign-in and on every navigation.
  useEffect(() => {
    if (!isAuthenticated) { setUnread(0); return; }
    refresh();
  }, [isAuthenticated, location.pathname, refresh]);

  // Slow poll, and an immediate refresh when the inbox marks something read.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const id = setInterval(refresh, UNREAD_POLL_MS);
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, refresh);
    return () => {
      clearInterval(id);
      window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, refresh);
    };
  }, [isAuthenticated, refresh]);

  if (!isAuthenticated) return null;

  const label = unread > 0
    ? `${t('notifications.title')}, ${unread} ${t('notifications.unread')}`
    : t('notifications.title');

  return (
    <Link to="/notifications" className={styles.bell} aria-label={label} data-testid="notification-bell">
      <span aria-hidden="true">🔔</span>
      {unread > 0 && (
        <span className={styles.badge} data-testid="notification-badge" aria-hidden="true">
          {unread > 99 ? '99+' : unread}
        </span>
      )}
    </Link>
  );
};

export default NotificationBell;
